import React, { useCallback, useEffect, useRef, useState } from "react";
import { X, Play, Pause, RotateCcw, Settings as SettingsIcon, Save, Trash2 } from "lucide-react";
import { useI18n } from "../context/I18nContext";

const BUILTIN = [
  { key: "arrows3", prep: 10, shoot: 90, orange: 30, red: 10 },
  { key: "arrows6", prep: 10, shoot: 180, orange: 30, red: 10 },
];

const LS_KEY = "bowlink_chrono_presets";
const fmt = (s) => `${Math.max(0, Math.ceil(s))}s`;

export default function ChronoModal({ onClose }) {
  const { t } = useI18n();

  const [prep, setPrep] = useState("10");
  const [shoot, setShoot] = useState("120");
  const [orange, setOrange] = useState("30");
  const [red, setRed] = useState("10");
  const [name, setName] = useState("");
  const [showErrors, setShowErrors] = useState(false);
  const [nameTried, setNameTried] = useState(false);

  const [phase, setPhase] = useState("idle"); // idle | prep | shoot | done
  const [remaining, setRemaining] = useState(0);
  const [paused, setPaused] = useState(false);

  const [saved, setSaved] = useState(() => {
    try { return JSON.parse(localStorage.getItem(LS_KEY) || "[]"); } catch { return []; }
  });

  const endRef = useRef(0);
  const pauseRef = useRef(0);
  const cfgRef = useRef({ prep: 10, shoot: 120, orange: 30, red: 10 });
  const acRef = useRef(null);
  const bufRef = useRef(null);
  const wakeRef = useRef(null);

  // ─── Validation (champs vides autorisés, on bloque juste le lancement) ───
  const nPrep = prep === "" ? null : parseInt(prep, 10);
  const nShoot = shoot === "" ? null : parseInt(shoot, 10);
  const nOrange = orange === "" ? null : parseInt(orange, 10);
  const nRed = red === "" ? null : parseInt(red, 10);
  const errPrep = nPrep === null;
  const errShoot = nShoot === null || nShoot < 1;
  const errOrange = nOrange === null || (nShoot !== null && nOrange > nShoot);
  const errRed = nRed === null || (nOrange !== null && nRed > nOrange);
  const invalid = errPrep || errShoot || errOrange || errRed;

  // ─── Audio ───
  const ensureAudio = useCallback(async () => {
    try {
      if (!acRef.current) {
        const AC = window.AudioContext || window.webkitAudioContext;
        acRef.current = new AC();
        const res = await fetch("/sounds/beep.wav");
        const arr = await res.arrayBuffer();
        bufRef.current = await acRef.current.decodeAudioData(arr);
      }
      if (acRef.current.state === "suspended") await acRef.current.resume();
    } catch { /* ignore */ }
  }, []);

  const beep = useCallback((times = 1) => {
    const ac = acRef.current, buf = bufRef.current;
    if (ac && buf) {
      for (let i = 0; i < times; i++) {
        const src = ac.createBufferSource();
        src.buffer = buf; src.connect(ac.destination);
        src.start(ac.currentTime + i * (buf.duration + 0.12));
      }
    } else {
      for (let i = 0; i < times; i++) {
        try { const a = new Audio("/sounds/beep.wav"); setTimeout(() => a.play().catch(() => {}), i * 1150); } catch { /* */ }
      }
    }
  }, []);

  // ─── Wake lock ───
  const acquireWake = useCallback(async () => {
    try { if ("wakeLock" in navigator) wakeRef.current = await navigator.wakeLock.request("screen"); } catch { /* */ }
  }, []);
  const releaseWake = useCallback(() => {
    try { wakeRef.current && wakeRef.current.release(); } catch { /* */ } wakeRef.current = null;
  }, []);

  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === "visible" && (phase === "prep" || phase === "shoot") && !paused) acquireWake();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [phase, paused, acquireWake]);

  useEffect(() => () => releaseWake(), [releaseWake]);

  // ─── Boucle ───
  const startShoot = () => {
    setPhase("shoot");
    endRef.current = Date.now() + cfgRef.current.shoot * 1000;
    setRemaining(cfgRef.current.shoot);
    beep(2);
  };

  useEffect(() => {
    if (phase === "idle" || phase === "done" || paused) return;
    const iv = setInterval(() => {
      const rem = (endRef.current - Date.now()) / 1000;
      if (rem <= 0) {
        if (phase === "prep") { startShoot(); }
        else { setPhase("done"); setRemaining(0); beep(3); releaseWake(); }
      } else {
        setRemaining(rem);
      }
    }, 100);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, paused]);

  const start = async () => {
    if (invalid) { setShowErrors(true); return; }
    cfgRef.current = { prep: nPrep, shoot: nShoot, orange: nOrange, red: nRed };
    await ensureAudio();
    await acquireWake();
    beep(1);
    if (nPrep > 0) {
      setPhase("prep");
      endRef.current = Date.now() + nPrep * 1000;
      setRemaining(nPrep);
    } else {
      startShoot();
    }
  };

  const pause = () => { pauseRef.current = (endRef.current - Date.now()) / 1000; setPaused(true); releaseWake(); };
  const resume = async () => { await acquireWake(); endRef.current = Date.now() + pauseRef.current * 1000; setPaused(false); };
  const reset = () => { setPhase("idle"); setPaused(false); setRemaining(0); releaseWake(); };
  const restart = async () => {
    await ensureAudio();
    await acquireWake();
    setPaused(false);
    beep(1);
    if (cfgRef.current.prep > 0) {
      setPhase("prep");
      endRef.current = Date.now() + cfgRef.current.prep * 1000;
      setRemaining(cfgRef.current.prep);
    } else {
      startShoot();
    }
  };

  const persist = (list) => { setSaved(list); try { localStorage.setItem(LS_KEY, JSON.stringify(list)); } catch { /* */ } };
  const savePreset = () => {
    if (invalid) { setShowErrors(true); return; }
    if (!name.trim()) { setNameTried(true); return; }
    persist([...saved, { name: name.trim(), prep: nPrep, shoot: nShoot, orange: nOrange, red: nRed }]);
    setName("");
  };
  const applyPreset = (p) => {
    setPrep(String(p.prep)); setShoot(String(p.shoot)); setOrange(String(p.orange)); setRed(String(p.red));
    setShowErrors(false);
  };
  const isSelected = (p) => nPrep === p.prep && nShoot === p.shoot && nOrange === p.orange && nRed === p.red;

  // ─── Couleur de fond selon la phase ───
  // En phase "idle" (config avant démarrage), on suit le thème via var CSS.
  // Les phases actives gardent leurs couleurs vives, indépendantes du thème.
  const cfg = cfgRef.current;
  let bg = "var(--bl-bg, #0a0a0a)", label = "";
  if (phase === "prep") { bg = "#4338ca"; label = t("chrono.prep_phase"); }
  else if (phase === "shoot") {
    if (remaining <= cfg.red) { bg = "#dc2626"; }
    else if (remaining <= cfg.orange) { bg = "#ea580c"; }
    else { bg = "#15803d"; }
    label = t("chrono.shoot_phase");
  } else if (phase === "done") { bg = "#7f1d1d"; label = t("chrono.done"); }

  const running = phase === "prep" || phase === "shoot";

  const numInput = (val, set, err) => (
    <input type="text" inputMode="numeric" value={val}
      onChange={(e) => { set(e.target.value.replace(/[^0-9]/g, "")); setShowErrors(false); }}
      className={`w-full bg-zinc-950 border outline-none rounded-md px-3 py-2 text-center font-heading font-black text-lg focus:border-cyan-400 ${showErrors && err ? "border-red-500" : "border-zinc-800"}`} />
  );

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 60, background: bg, transition: "background 300ms", display: "flex", flexDirection: "column" }}>
      {/* Barre haut */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 16px" }}>
        <span style={{ color: "rgba(255,255,255,0.85)", fontWeight: 800, textTransform: "uppercase", letterSpacing: ".2em", fontSize: "13px" }}>
          {t("chrono.title")}
        </span>
        <button onClick={() => { reset(); onClose(); }} style={{ background: "rgba(0,0,0,0.25)", border: "none", color: "#fff", borderRadius: "8px", padding: "8px", cursor: "pointer" }}><X size={18} /></button>
      </div>

      {phase === "idle" ? (
        // ─── Configuration ───
        <div style={{ flex: 1, overflowY: "auto", padding: "8px 16px 24px" }}>
          <div style={{ maxWidth: "460px", margin: "0 auto", display: "flex", flexDirection: "column", gap: "20px" }}>
            <div>
              <div style={{ color: "#a1a1aa", fontSize: "11px", textTransform: "uppercase", letterSpacing: ".2em", marginBottom: "8px" }}>{t("chrono.presets")}</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                {BUILTIN.map((p) => (
                  <button key={p.key} onClick={() => applyPreset(p)}
                    className={`px-3 py-2 rounded-md text-xs font-bold uppercase tracking-wide bg-zinc-900 border ${isSelected(p) ? "border-cyan-400 text-cyan-400" : "border-zinc-800 text-zinc-200 hover:border-cyan-400/60"}`}>
                    {t(`chrono.${p.key}`)}
                  </button>
                ))}
              </div>
            </div>

            {saved.length > 0 && (
              <div>
                <div style={{ color: "#a1a1aa", fontSize: "11px", textTransform: "uppercase", letterSpacing: ".2em", marginBottom: "8px" }}>{t("chrono.my_presets")}</div>
                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                  {saved.map((p, i) => (
                    <div key={i} className={`flex items-center gap-2 bg-zinc-900 border rounded-md px-3 py-2 ${isSelected(p) ? "border-cyan-400" : "border-zinc-800"}`}>
                      <button onClick={() => applyPreset(p)} className={`flex-1 text-left text-sm font-bold ${isSelected(p) ? "text-cyan-400" : "text-zinc-100"}`}>{p.name}</button>
                      <span className="text-xs text-zinc-500">{fmt(p.shoot)}</span>
                      <button onClick={() => persist(saved.filter((_, k) => k !== i))} className="p-1 text-zinc-500 hover:text-red-400"><Trash2 size={15} /></button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div><div className="text-[11px] uppercase tracking-[0.2em] text-zinc-400 mb-1">{t("chrono.prep")}</div>{numInput(prep, setPrep, errPrep)}</div>
              <div><div className="text-[11px] uppercase tracking-[0.2em] text-zinc-400 mb-1">{t("chrono.shoot")}</div>{numInput(shoot, setShoot, errShoot)}</div>
              <div><div className="text-[11px] uppercase tracking-[0.2em] text-orange-400 mb-1">{t("chrono.orange")}</div>{numInput(orange, setOrange, errOrange)}</div>
              <div><div className="text-[11px] uppercase tracking-[0.2em] text-red-400 mb-1">{t("chrono.red")}</div>{numInput(red, setRed, errRed)}</div>
            </div>
            <div className="text-xs text-zinc-500">{t("chrono.times_hint")}</div>
            {showErrors && invalid && <div className="text-xs text-red-400 font-bold">{t("chrono.fix_fields")}</div>}

            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-400 mb-1">{t("chrono.name")}</div>
              <input value={name} onChange={(e) => { setName(e.target.value); setNameTried(false); }} placeholder={t("chrono.name_ph")}
                className={`w-full bg-zinc-950 border outline-none rounded-md px-3 py-2 focus:border-cyan-400 ${nameTried && !name.trim() ? "border-red-500" : "border-zinc-800"}`} />
            </div>

            <div className="flex gap-2">
              <button onClick={savePreset} className="inline-flex items-center gap-2 bg-zinc-900 border border-zinc-800 hover:border-zinc-600 text-zinc-200 font-bold uppercase tracking-wide px-4 py-3 rounded-md text-sm">
                <Save size={16} /> {t("chrono.save")}
              </button>
              <button onClick={start} disabled={invalid}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-cyan-400 text-black hover:bg-cyan-300 disabled:opacity-40 font-bold uppercase tracking-wide px-6 py-3 rounded-md">
                <Play size={18} /> {t("chrono.start")}
              </button>
            </div>
          </div>
        </div>
      ) : (
        // ─── Chrono en cours / terminé ───
        <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "24px", padding: "16px" }}>
          <div style={{ color: "rgba(255,255,255,0.9)", textTransform: "uppercase", letterSpacing: ".3em", fontWeight: 800, fontSize: "clamp(14px,4vw,22px)" }}>{label}</div>
          <div style={{ color: "#fff", fontWeight: 900, fontFamily: "monospace", lineHeight: 1, fontSize: "clamp(80px,26vw,240px)" }}>
            {phase === "done" ? "0s" : fmt(remaining)}
          </div>
          <div style={{ display: "flex", gap: "12px" }}>
            {running && !paused && (
              <button onClick={pause} className="inline-flex items-center gap-2 bg-black/30 hover:bg-black/40 text-[#fff] font-bold uppercase tracking-wide px-8 py-4 rounded-md text-lg"><Pause size={22} /> {t("chrono.pause")}</button>
            )}
            {running && paused && (
              <button onClick={resume} className="inline-flex items-center gap-2 bg-black/30 hover:bg-black/40 text-[#fff] font-bold uppercase tracking-wide px-8 py-4 rounded-md text-lg"><Play size={22} /> {t("chrono.resume")}</button>
            )}
            <button onClick={restart} className="inline-flex items-center gap-2 bg-black/30 hover:bg-black/40 text-[#fff] font-bold uppercase tracking-wide px-8 py-4 rounded-md text-lg"><RotateCcw size={22} /> {t("chrono.restart")}</button>
            <button onClick={reset} className="inline-flex items-center gap-2 bg-black/30 hover:bg-black/40 text-[#fff] font-bold uppercase tracking-wide px-8 py-4 rounded-md text-lg"><SettingsIcon size={22} /> {t("chrono.settings")}</button>
          </div>
        </div>
      )}
    </div>
  );
}
